import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {
    CircularProgress,
    TextField,
    Typography,
    Box,
    Tab,
    Tabs,
    Chip,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Card,
    CardContent,
    Button
} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../Services/ServiceLocator";
import { v4 as uuid } from 'uuid';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faUndo, faCalculator, faCopy, faTimes, faSync} from "@fortawesome/free-solid-svg-icons";
import WorkshopTemplate from "./WorkshopTemplate";
import { AuthenticationService } from "../../Services/AuthenticationService";
import { toUtcForStorage } from '../../utils/dateUtils';
import { getPartLinkPartId, getPartParentRelations, getPartInventorySessionBlockers, getPartParentConfirmationBlockers } from '../../utils/bomContributions';

let globalProps;

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    filterToggle: {
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '2px 10px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'white !important',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        minWidth: '150px',
        '& .MuiCheckbox-root': {
            padding: '0px',
            color: '#35d2d9 !important',
            '&.Mui-checked': {
                color: '#35d2d9 !important',
            },
            '&:hover': {
                backgroundColor: 'white',
            },
        }
    },
    filterLabel: {
        marginLeft: '8px',
        fontSize: '14px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    itemCard: {
        marginBottom: '4px',
        border: '1px solid #e0e0e0',
        '&.modified': {
            borderColor: '#ff9800',
            backgroundColor: '#fff3e0',
        },
        '&.confirmed': {
            borderColor: '#4caf50',
            backgroundColor: '#e8f5e8',
        },
        '&.saving': {
            borderColor: '#2196f3',
            backgroundColor: '#e3f2fd',
        }
    },
    itemHeader: {
        marginBottom: '8px',
    },
    itemTitle: {
        fontWeight: 'bold',
        fontSize: '16px',
    },
    itemSubtitle: {
        color: '#666',
        fontSize: '14px',
    },
    quantitySection: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    },
    actionSection: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    }
});

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`inventory-session-tabpanel-${index}`}
            aria-labelledby={`inventory-session-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 1 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

class InventorySession extends React.Component {
    constructor(props) {
        super(props);
        globalProps = props;
    }

    state = {
        loading: true,
        user: {},
        assemblies: [],
        subAssemblies: [],
        parts: [],
        activeTab: 0,
        modifiedRows: {},
        confirmedRows: {},
        savingRows: {},
        selectedRows: {},
        collaborativeState: {
            packageLogs: [],
            inventoryLogs: [],
            lastRefresh: null
        },
        hideConfirmedRows: false,
        showNonInventoryItems: false,
        searchTerm: '',
        calculatorOpen: false,
        calculatorRowKey: null,
        calculatorTitle: '',
        calculatorLines: [0],
        calculatorTotal: 0,
        // Track if this is the first load to store original quantities
        isFirstLoad: true,
        // Track last data refresh time
        lastDataRefresh: null,
        // Track manually modified rows (user input only, not sync updates)
        manuallyModifiedRows: {},
        // Track sync state
        syncing: false
    };

    componentDidMount() {
        const authService = AuthenticationService.getInstance();
        const token = authService.getToken();
        if (token) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
        }
        
        this.loadStoredState();
        this.loadData();
        this.startCollaborativeRefresh();
    }

    componentWillUnmount() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
    }

    loadStoredState = () => {
        const storedState = localStorage.getItem('inventorySetupState' + this.getSessionId());
        if (storedState) {
            try {
                const parsed = JSON.parse(storedState);
                this.setState({
                    modifiedRows: parsed.modifiedRows || {},
                    confirmedRows: parsed.confirmedRows || {},
                    activeTab: parsed.activeTab || 0,
                    hideConfirmedRows: parsed.hideConfirmedRows || false,
                    showNonInventoryItems: parsed.showNonInventoryItems || false,
                    manuallyModifiedRows: parsed.manuallyModifiedRows || {},
                    searchTerm: parsed.searchTerm || ''
                });
            } catch (e) {
                console.warn('Failed to parse stored inventory setup state:', e);
            }
        }
    }

    saveStateToStorage = () => {
        const stateToSave = {
            modifiedRows: this.state.modifiedRows,
            confirmedRows: this.state.confirmedRows,
            activeTab: this.state.activeTab,
            hideConfirmedRows: this.state.hideConfirmedRows,
            showNonInventoryItems: this.state.showNonInventoryItems,
            manuallyModifiedRows: this.state.manuallyModifiedRows,
            searchTerm: this.state.searchTerm
        };
        localStorage.setItem('inventorySetupState' + this.getSessionId(), JSON.stringify(stateToSave));
    }

    startCollaborativeRefresh = () => {
        this.loadCollaborativeState();
        this.refreshTimer = setInterval(() => {
            this.loadCollaborativeState();
            // Also refresh main data every 30 seconds
            this.loadData();
        }, 30000); // Refresh every 30 seconds
    }

    loadCollaborativeState = async () => {
        try {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            const partService = ServiceLocator.Instance.getPartService();
            const sessionId = this.getSessionId();
            const sessionReason = `Inventory Session ${sessionId}`;
            
            // Load package logs for assemblies and subassemblies with session-specific reason
            const packageLogs = await assemblyService.getPackageLogs();
            const initialPackageLogs = packageLogs.filter(log => 
                log.reason === sessionReason
            );
            
            // Load inventory logs for parts with session-specific reason
            const inventoryLogs = await partService.getInventoryLogs();
            const initialInventoryLogs = inventoryLogs.filter(log => 
                log.reason === sessionReason
            );

            this.setState({
                collaborativeState: {
                    packageLogs: initialPackageLogs,
                    inventoryLogs: initialInventoryLogs,
                    lastRefresh: new Date()
                }
            });

            this.syncCollaborativeStateWithLocal();
        } catch (error) {
            console.warn('Failed to load collaborative state:', error);
        }
    }

    syncCollaborativeStateWithLocal = () => {
        const { packageLogs, inventoryLogs } = this.state.collaborativeState;
        const confirmedRows = { ...this.state.confirmedRows };

        // Mark rows as confirmed based on package logs
        packageLogs.forEach(log => {
            if (log.assemblyId) {
                const rowKey = `assembly|${log.assemblyId}`;
                confirmedRows[rowKey] = true;
            }
            if (log.subAssemblyId) {
                const rowKey = `subassembly|${log.subAssemblyId}`;
                confirmedRows[rowKey] = true;
            }
        });

        // Mark rows as confirmed based on inventory logs
        inventoryLogs.forEach(log => {
            const rowKey = `part|${log.partId}`;
            confirmedRows[rowKey] = true;
        });

        this.setState({ confirmedRows });
    }

    isChildItem = (itemType, itemId) => {
        if (itemType === 'subassembly') {
            return this.state.assemblies.some(assembly =>
                assembly.assembly.partsToAssemblies &&
                assembly.assembly.partsToAssemblies.some(pta => pta.subAssemblyId === itemId)
            );
        }
        if (itemType === 'part') {
            return getPartParentRelations(itemId, this.state.assemblies, this.state.subAssemblies).length > 0;
        }
        return false;
    }

    areParentsConfirmed = (itemType, itemId) => {
        if (itemType === 'assembly') {
            return true;
        }

        const parentsList = [];

        if (itemType === 'subassembly') {
            this.state.assemblies.forEach(assembly => {
                if (assembly.assembly.partsToAssemblies) {
                    assembly.assembly.partsToAssemblies.forEach(pta => {
                        if (pta.subAssemblyId === itemId) {
                            const isConfirmed = this.isRowConfirmed(`assembly|${assembly.assembly.id}`);
                            parentsList.push({ type: 'assembly', id: assembly.assembly.assemblyId, confirmed: isConfirmed });
                        }
                    });
                }
            });
        } else if (itemType === 'part') {
            return getPartParentConfirmationBlockers(
                itemId,
                this.state.assemblies,
                this.state.subAssemblies,
                (key) => this.isRowConfirmed(key)
            ).length === 0;
        }

        if (parentsList.length === 0) {
            return true;
        }

        return parentsList.every(parent => parent.confirmed);
    }

    getPartSessionBlockers = (partRow) => {
        return getPartInventorySessionBlockers(
            partRow,
            this.state.assemblies,
            this.state.subAssemblies,
            {
                showNonInventoryItems: this.state.showNonInventoryItems,
                isRowConfirmed: (key) => this.isRowConfirmed(key),
            }
        );
    }

    getHiddenPartsMatchingSearch = () => {
        const term = (this.state.searchTerm || '').trim().toLowerCase();
        if (!term) return [];

        return (this.state.parts || []).filter((partRow) => {
            const partId = partRow?.part?.partId;
            if (!partId || !partId.toLowerCase().includes(term)) return false;

            const rowKey = `part|${partRow.part.id}`;
            if (this.state.hideConfirmedRows && this.isRowConfirmed(rowKey)) return false;

            const blockers = this.getPartSessionBlockers(partRow);
            return blockers.length > 0;
        });
    }

    toggleHideConfirmedRows = () => {
        this.setState(prevState => ({
            hideConfirmedRows: !prevState.hideConfirmedRows
        }), () => {
            this.saveStateToStorage();
        });
    }

    toggleShowNonInventoryItems = () => {
        this.setState(prevState => ({
            showNonInventoryItems: !prevState.showNonInventoryItems
        }), () => {
            this.saveStateToStorage();
        });
    }

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Purchase')) {
            globalProps.history.push('/login');
            return;
        }

        self.setState({syncing: true});
        Promise.all([self.loadAssemblies(), self.loadSubAssemblies(), self.loadParts()]).then(() => {
            self.setState({
                loading: false, 
                syncing: false,
                user, 
                permissions: user.userPermissions,
                isFirstLoad: false,
                lastDataRefresh: new Date()
            });
        });
    }

    // Force sync data from database, bypassing cache and updating quantities while preserving confirmed state
    syncDataFromDatabase = async () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Purchase')) {
            globalProps.history.push('/login');
            return;
        }
        self.setState({syncing: true});
        
        try {
            // Preserve view state and clear row-level changes
            const preservedState = {
                modifiedRows: {},
                confirmedRows: {},
                manuallyModifiedRows: {},
                activeTab: this.state.activeTab,
                hideConfirmedRows: this.state.hideConfirmedRows,
                showNonInventoryItems: this.state.showNonInventoryItems,
                searchTerm: this.state.searchTerm
            };
            localStorage.setItem('inventorySetupState' + this.getSessionId(), JSON.stringify(preservedState));
            // Clear cached data timestamps so services fetch fresh data
            localStorage.removeItem('assembliesUpdated');
            localStorage.removeItem('subAssembliesUpdated');
            localStorage.removeItem('partsUpdated');
            this.loadStoredState();
            this.loadData();
    
            /*
            // Force refresh all data from database (bypassing cache)
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            const partService = ServiceLocator.Instance.getPartService();
            
            // Clear cached data from localStorage to force fresh data
            localStorage.removeItem('assembliesUpdated');
            localStorage.removeItem('subAssembliesUpdated');
            localStorage.removeItem('partsUpdated');
            
            // Clear any cached data by calling the services directly
            const [assemblies, subAssemblies, parts] = await Promise.all([
                assemblyService.getAllAssembliesExpanded(),
                assemblyService.getAllSubAssembliesExpanded(),
                partService.getAllPartsExpanded()
            ]);

            // Sort and filter the data
            const sortedAssemblies = assemblies.sort((a, b) => {
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                if (b.assembly.assemblyId > a.assembly.assemblyId) return -1;
                if (a.assembly.revision > b.assembly.revision) return -1;
                if (b.assembly.revision > a.assembly.revision) return 1;
                return 0;
            }).filter(x => x.assembly.isActive);

            const sortedSubAssemblies = subAssemblies.sort((a, b) => {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                if (a.subAssembly.revision > b.subAssembly.revision) return -1;
                if (b.subAssembly.revision > a.subAssembly.revision) return 1;
                return 0;
            }).filter(x => x.subAssembly.isActive);

            const sortedParts = parts.filter(x => x.part.isActive).sort((a, b) => {
                if (a.part.partId > b.part.partId) return 1;
                if (b.part.partId > a.part.partId) return -1;
                if (a.part.revision > b.part.revision) return -1;
                if (b.part.revision > a.part.revision) return 1;
                return 0;
            });

            // Update modifiedRows with new quantities while preserving confirmed state
            const modifiedRows = {...self.state.modifiedRows};
            const confirmedRows = {...self.state.confirmedRows};
            const manuallyModifiedRows = {...self.state.manuallyModifiedRows};

            // Update assemblies
            sortedAssemblies.forEach(assembly => {
                const key = `assembly|${assembly.assembly.id}`;
                const currentQuantity = assembly.totalAssembledOnHandWithRev;
                const isConfirmed = confirmedRows[key];
                
                if (!modifiedRows[key]) {
                    // First time seeing this assembly
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else {
                    // Update the current value to reflect latest database state
                    // If confirmed, update both original and current to maintain the confirmed state
                    if (isConfirmed) {
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                        // Clear manual modification flag since we're syncing from database
                        manuallyModifiedRows[key] = false;
                    } else {
                        // Not confirmed - update both current and original values since sync represents new baseline
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                    }
                }
            });

            // Update subassemblies
            sortedSubAssemblies.forEach(subAssembly => {
                const key = `subassembly|${subAssembly.subAssembly.id}`;
                const currentQuantity = subAssembly.totalAssembledOnHandWithRev;
                const isConfirmed = confirmedRows[key];
                
                if (!modifiedRows[key]) {
                    // First time seeing this subassembly
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else {
                    // Update the current value to reflect latest database state
                    // If confirmed, update both original and current to maintain the confirmed state
                    if (isConfirmed) {
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                        // Clear manual modification flag since we're syncing from database
                        manuallyModifiedRows[key] = false;
                    } else {
                        // Not confirmed - update both current and original values since sync represents new baseline
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                    }
                }
            });

            // Update parts
            sortedParts.forEach(part => {
                const key = `part|${part.part.id}`;
                const currentQuantity = part.totalUnpackaged;
                const isConfirmed = confirmedRows[key];
                
                if (!modifiedRows[key]) {
                    // First time seeing this part
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else {
                    // Update the current value to reflect latest database state
                    // If confirmed, update both original and current to maintain the confirmed state
                    if (isConfirmed) {
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                        // Clear manual modification flag since we're syncing from database
                        manuallyModifiedRows[key] = false;
                    } else {
                        // Not confirmed - update both current and original values since sync represents new baseline
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                    }
                }
            });

            // Update state with new data
            self.setState({
                assemblies: sortedAssemblies,
                subAssemblies: sortedSubAssemblies,
                parts: sortedParts,
                modifiedRows,
                manuallyModifiedRows,
                syncing: false,
                lastDataRefresh: new Date()
            }, () => {
                // Save updated state to storage
                this.saveStateToStorage();
            });

            // Also refresh collaborative state
            await this.loadCollaborativeState();

            window.$alertSuccess('Inventory data synced from database successfully');
            */
        } catch (error) {
            console.error('Error syncing data from database:', error);
            window.$alertError('Error syncing data from database');
            self.setState({syncing: false});
        }
    }

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getAllAssembliesExpanded().then((assemblies) => {
            assemblies = assemblies.sort(function (a, b) {
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                if (b.assembly.assemblyId > a.assembly.assemblyId) return -1;
                if (a.assembly.revision > b.assembly.revision) return -1;
                if (b.assembly.revision > a.assembly.revision) return 1;
                return 0;
            });
            
            const modifiedRows = {...self.state.modifiedRows};
            const isFirstLoad = self.state.isFirstLoad;
            
            assemblies.forEach(assembly => {
                const key = `assembly|${assembly.assembly.id}`;
                const currentQuantity = assembly.totalAssembledOnHandWithRev;
                
                if (!modifiedRows[key]) {
                    // First time loading this assembly - store original quantity
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else if (!isFirstLoad) {
                    // Not first load - update current value to reflect latest data
                    // but preserve original value for delta calculations
                    // Only update if the row is not modified or confirmed
                    const isModified = self.isRowModified(key);
                    const isConfirmed = self.isRowConfirmed(key);
                    
                    if (!isModified && !isConfirmed) {
                        modifiedRows[key] = {
                            ...modifiedRows[key],
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            self.setState({assemblies: assemblies.filter(x => x.assembly.isActive), modifiedRows});
        });
    }

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getAllSubAssembliesExpanded().then((subAssemblies) => {
            subAssemblies = subAssemblies.sort(function (a, b) {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                if (a.subAssembly.revision > b.subAssembly.revision) return -1;
                if (b.subAssembly.revision > a.subAssembly.revision) return 1;
                return 0;
            });
            
            const modifiedRows = {...self.state.modifiedRows};
            const isFirstLoad = self.state.isFirstLoad;
            
            subAssemblies.forEach(subAssembly => {
                const key = `subassembly|${subAssembly.subAssembly.id}`;
                const currentQuantity = subAssembly.totalAssembledOnHandWithRev;
                
                if (!modifiedRows[key]) {
                    // First time loading this subassembly - store original quantity
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else if (!isFirstLoad) {
                    // Not first load - update current value to reflect latest data
                    // but preserve original value for delta calculations
                    // Only update if the row is not modified or confirmed
                    const isModified = self.isRowModified(key);
                    const isConfirmed = self.isRowConfirmed(key);
                    
                    if (!isModified && !isConfirmed) {
                        modifiedRows[key] = {
                            ...modifiedRows[key],
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            self.setState({subAssemblies: subAssemblies.filter(x => x.subAssembly.isActive), modifiedRows});
        });
    }

    loadParts = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getAllPartsExpanded().then((parts) => {
            parts = parts.filter(x => x.part.isActive).sort(function (a, b) {
                if (a.part.partId > b.part.partId) return 1;
                if (b.part.partId > a.part.partId) return -1;
                if (a.part.revision > b.part.revision) return -1;
                if (b.part.revision > a.part.revision) return 1;
                return 0;
            });
            
            const modifiedRows = {...self.state.modifiedRows};
            const isFirstLoad = self.state.isFirstLoad;
            
            parts.forEach(part => {
                const key = `part|${part.part.id}`;
                const currentQuantity = part.totalUnpackaged;
                
                if (!modifiedRows[key]) {
                    // First time loading this part - store original quantity
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else if (!isFirstLoad) {
                    // Not first load - update current value to reflect latest data
                    // but preserve original value for delta calculations
                    // Only update if the row is not modified or confirmed
                    const isModified = self.isRowModified(key);
                    const isConfirmed = self.isRowConfirmed(key);
                    
                    if (!isModified && !isConfirmed) {
                        modifiedRows[key] = {
                            ...modifiedRows[key],
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            self.setState({parts, modifiedRows});
        });
    }

    handleTabChange = (event, newValue) => {
        this.setState({activeTab: newValue});
    }

    searchInventory = (event) => {
        const searchInput = event.target.value;
        this.setState({searchTerm: searchInput});
    }

    handleQuantityChange = (rowKey, newValue) => {
        const modifiedRows = {...this.state.modifiedRows};
        const confirmedRows = {...this.state.confirmedRows};
        const manuallyModifiedRows = {...this.state.manuallyModifiedRows};
        
        const newValueParsed = parseFloat(newValue) || 0;
        
        // If quantity changed after being confirmed, remove confirmation and update originalValue
        if (confirmedRows[rowKey] && modifiedRows[rowKey].originalValue !== newValueParsed) {
            confirmedRows[rowKey] = false;
            // Update originalValue to current value for correction logs
            modifiedRows[rowKey] = {
                ...modifiedRows[rowKey],
                originalValue: modifiedRows[rowKey].currentValue,
                currentValue: newValueParsed
            };
        } else {
            // Normal quantity change
            modifiedRows[rowKey] = {
                ...modifiedRows[rowKey],
                currentValue: newValueParsed
            };
        }
        
        // Mark as manually modified by user
        manuallyModifiedRows[rowKey] = true;
        
        this.setState({modifiedRows, confirmedRows, manuallyModifiedRows}, () => {
            this.saveStateToStorage();
        });
    }

    handleRowSelection = (rowKey, checked) => {
        const selectedRows = {...this.state.selectedRows};
        if (checked) {
            selectedRows[rowKey] = true;
        } else {
            delete selectedRows[rowKey];
        }
        this.setState({selectedRows});
    }

    handleSelectAll = () => {
        const { activeTab, searchTerm, hideConfirmedRows } = this.state;
        let filteredItems = [];
        
        if (activeTab === 0) {
            // Assemblies
            filteredItems = [...this.state.assemblies].sort((a, b) => {
                const aId = a.assembly.assemblyId || '';
                const bId = b.assembly.assemblyId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.assembly.revision || '';
                const bRev = b.assembly.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(assembly => {
                const rowKey = `assembly|${assembly.assembly.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (!this.state.showNonInventoryItems && !assembly.assembly.isInventoryItem) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = assembly.assembly.assemblyId && assembly.assembly.assemblyId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = assembly.assembly.description && assembly.assembly.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                return true;
            });
        } else if (activeTab === 1) {
            // Subassemblies
            filteredItems = [...this.state.subAssemblies].sort((a, b) => {
                const aId = a.subAssembly.subAssemblyId || '';
                const bId = b.subAssembly.subAssemblyId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.subAssembly.revision || '';
                const bRev = b.subAssembly.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(subAssembly => {
                const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (!this.state.showNonInventoryItems && !subAssembly.subAssembly.isInventoryItem) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = subAssembly.subAssembly.subAssemblyId && subAssembly.subAssembly.subAssemblyId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = subAssembly.subAssembly.description && subAssembly.subAssembly.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                const parentsConfirmed = this.areParentsConfirmed('subassembly', subAssembly.subAssembly.id);
                return parentsConfirmed;
            });
        } else if (activeTab === 2) {
            // Parts
            filteredItems = [...this.state.parts].sort((a, b) => {
                const aId = a.part.partId || '';
                const bId = b.part.partId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.part.revision || '';
                const bRev = b.part.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(part => {
                const rowKey = `part|${part.part.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (!this.state.showNonInventoryItems && !part.part.isInventoryItem) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = part.part.partId && part.part.partId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = part.part.description && part.part.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                const parentsConfirmed = this.areParentsConfirmed('part', part.part.id);
                return parentsConfirmed;
            });
        }
        
        // Select all filtered items
        const selectedRows = {...this.state.selectedRows};
        filteredItems.forEach(item => {
            let rowKey;
            if (activeTab === 0) {
                rowKey = `assembly|${item.assembly.id}`;
            } else if (activeTab === 1) {
                rowKey = `subassembly|${item.subAssembly.id}`;
            } else if (activeTab === 2) {
                rowKey = `part|${item.part.id}`;
            }
            selectedRows[rowKey] = true;
        });
        
        this.setState({selectedRows});
    }

    handleSelectNone = () => {
        const { activeTab, searchTerm, hideConfirmedRows } = this.state;
        let filteredItems = [];
        
        if (activeTab === 0) {
            // Assemblies
            filteredItems = [...this.state.assemblies].sort((a, b) => {
                const aId = a.assembly.assemblyId || '';
                const bId = b.assembly.assemblyId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.assembly.revision || '';
                const bRev = b.assembly.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(assembly => {
                const rowKey = `assembly|${assembly.assembly.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (!this.state.showNonInventoryItems && !assembly.assembly.isInventoryItem) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = assembly.assembly.assemblyId && assembly.assembly.assemblyId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = assembly.assembly.description && assembly.assembly.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                return true;
            });
        } else if (activeTab === 1) {
            // Subassemblies
            filteredItems = [...this.state.subAssemblies].sort((a, b) => {
                const aId = a.subAssembly.subAssemblyId || '';
                const bId = b.subAssembly.subAssemblyId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.subAssembly.revision || '';
                const bRev = b.subAssembly.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(subAssembly => {
                const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (!this.state.showNonInventoryItems && !subAssembly.subAssembly.isInventoryItem) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = subAssembly.subAssembly.subAssemblyId && subAssembly.subAssembly.subAssemblyId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = subAssembly.subAssembly.description && subAssembly.subAssembly.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                const parentsConfirmed = this.areParentsConfirmed('subassembly', subAssembly.subAssembly.id);
                return parentsConfirmed;
            });
        } else if (activeTab === 2) {
            // Parts
            filteredItems = [...this.state.parts].sort((a, b) => {
                const aId = a.part.partId || '';
                const bId = b.part.partId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.part.revision || '';
                const bRev = b.part.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(part => {
                const rowKey = `part|${part.part.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (!this.state.showNonInventoryItems && !part.part.isInventoryItem) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = part.part.partId && part.part.partId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = part.part.description && part.part.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                const parentsConfirmed = this.areParentsConfirmed('part', part.part.id);
                return parentsConfirmed;
            });
        }
        
        // Deselect all filtered items
        const selectedRows = {...this.state.selectedRows};
        filteredItems.forEach(item => {
            let rowKey;
            if (activeTab === 0) {
                rowKey = `assembly|${item.assembly.id}`;
            } else if (activeTab === 1) {
                rowKey = `subassembly|${item.subAssembly.id}`;
            } else if (activeTab === 2) {
                rowKey = `part|${item.part.id}`;
            }
            delete selectedRows[rowKey];
        });
        
        this.setState({selectedRows});
    }

    handleResetSelected = async () => {
        const selectedRowKeys = Object.keys(this.state.selectedRows).filter(key => this.state.selectedRows[key]);
        
        if (selectedRowKeys.length === 0) {
            window.$alertError("No rows selected");
            return;
        }
        
        if (window.confirm(`Reset ${selectedRowKeys.length} selected item(s)?`)) {
            const savingRows = {...this.state.savingRows};
            selectedRowKeys.forEach(rowKey => {
                savingRows[rowKey] = true;
            });
            this.setState({savingRows});
            
            try {
                // Process each selected row
                for (const rowKey of selectedRowKeys) {
                    const [type, id] = rowKey.split('|');
                    await this.unconfirmRow(rowKey, type, id, false);
                }
                
                // Clear selections after successful reset
                this.setState({selectedRows: {}});
                window.$alertSuccess(`Reset ${selectedRowKeys.length} item(s)`);
            } catch (error) {
                console.error('Error resetting selected rows:', error);
                window.$alertError('Error resetting selected items');
            } finally {
                // Clear saving state
                const savingRows = {...this.state.savingRows};
                selectedRowKeys.forEach(rowKey => {
                    savingRows[rowKey] = false;
                });
                this.setState({savingRows});

                setTimeout(() => {
                    this.syncDataFromDatabase();
                }, 1000);
            }
        }
    }

    handleConfirmSelected = async () => {
        const selectedRowKeys = Object.keys(this.state.selectedRows).filter(key => this.state.selectedRows[key]);
        
        if (selectedRowKeys.length === 0) {
            window.$alertError("No rows selected");
            return;
        }
        
        if (window.confirm(`Confirm ${selectedRowKeys.length} selected item(s)?`)) {
            const savingRows = {...this.state.savingRows};
            selectedRowKeys.forEach(rowKey => {
                savingRows[rowKey] = true;
            });
            this.setState({savingRows});
            
            try {
                // Process each selected row
                for (const rowKey of selectedRowKeys) {
                    const [type, id] = rowKey.split('|');
                    await this.confirmRow(rowKey, type, id);
                }
                
                // Clear selections after successful confirmation
                this.setState({selectedRows: {}});
                window.$alertSuccess(`Confirmed ${selectedRowKeys.length} item(s)`);
            } catch (error) {
                console.error('Error confirming selected rows:', error);
                window.$alertError('Error confirming selected items');
            } finally {
                // Clear saving state
                const savingRows = {...this.state.savingRows};
                selectedRowKeys.forEach(rowKey => {
                    savingRows[rowKey] = false;
                });
                this.setState({savingRows});
            }
        }
    }

    openCalculator = (rowKey, title) => {
        const modifiedRows = this.state.modifiedRows;
        const currentValue = modifiedRows[rowKey] ? modifiedRows[rowKey].currentValue : 0;
        
        this.setState({
            calculatorOpen: true,
            calculatorRowKey: rowKey,
            calculatorTitle: title,
            calculatorLines: [0],
            calculatorTotal: 0
        });
    }

    closeCalculator = () => {
        this.setState({
            calculatorOpen: false,
            calculatorRowKey: null,
            calculatorTitle: '',
            calculatorLines: [0],
            calculatorTotal: 0
        });
    }

    updateCalculatorLine = (index, value) => {
        const lines = [...this.state.calculatorLines];
        lines[index] = parseFloat(value) || 0;
        const total = lines.reduce((sum, line) => sum + line, 0);
        
        this.setState({
            calculatorLines: lines,
            calculatorTotal: total
        });
    }

    addCalculatorLine = () => {
        this.setState({
            calculatorLines: [...this.state.calculatorLines, 0],
            calculatorTotal: this.state.calculatorTotal + 0
        }, () => {
            // Focus the new input after state update
            setTimeout(() => {
                const newInput = document.querySelector(`input[data-calculator-index="${this.state.calculatorLines.length - 1}"]`);
                if (newInput) {
                    newInput.focus();
                }
            }, 100);
        });
    }

    handleCalculatorKeyPress = (event, index) => {
        if (event.key === 'Tab' && index === this.state.calculatorLines.length - 1) {
            event.preventDefault();
            this.setState({
                calculatorLines: [...this.state.calculatorLines, 0]
            }, () => {
                // Focus the new input after state update
                setTimeout(() => {
                    const newInput = document.querySelector(`input[data-calculator-index="${this.state.calculatorLines.length - 1}"]`);
                    if (newInput) {
                        newInput.focus();
                    }
                }, 100);
            });
        }
    }

    confirmCalculator = () => {
        if (window.confirm("Are you sure you're done?")) {
            this.handleQuantityChange(this.state.calculatorRowKey, this.state.calculatorTotal);
            this.closeCalculator();
            
            // Auto-confirm the row after setting the quantity
            const rowKey = this.state.calculatorRowKey;
            const rowType = rowKey.split('|')[0]; // 'assembly', 'subassembly', or 'part'
            let itemId;
            
            if (rowType === 'assembly') {
                const assembly = this.state.assemblies.find(a => `assembly|${a.assembly.id}` === rowKey);
                itemId = assembly?.assembly.id;
            } else if (rowType === 'subassembly') {
                const subAssembly = this.state.subAssemblies.find(sa => `subassembly|${sa.subAssembly.id}` === rowKey);
                itemId = subAssembly?.subAssembly.id;
            } else if (rowType === 'part') {
                const part = this.state.parts.find(p => `part|${p.part.id}` === rowKey);
                itemId = part?.part.id;
            }
            
            if (itemId) {
                // Small delay to ensure quantity change is processed first
                setTimeout(() => {
                    this.confirmRow(rowKey, rowType, itemId);
                }, 100);
            }
        }
    }

    isRowModified = (rowKey) => {
        // Only consider a row modified if it was manually modified by the user
        return this.state.manuallyModifiedRows[rowKey] === true;
    }

    isRowConfirmed = (rowKey) => {
        return this.state.confirmedRows[rowKey] || false;
    }

    isRowSaving = (rowKey) => {
        return this.state.savingRows[rowKey] || false;
    }

    getItemByRowKey = (rowKey) => {
        const [type, id] = rowKey.split('|');
        if (type === 'assembly') {
            return this.state.assemblies.find(a => a.assembly.id === id);
        } else if (type === 'subassembly') {
            return this.state.subAssemblies.find(sa => sa.subAssembly.id === id);
        } else if (type === 'part') {
            return this.state.parts.find(p => p.part.id === id);
        }
        return null;
    }

    confirmRow = async (rowKey, type, id) => {
        const self = this;
        const rowData = this.state.modifiedRows[rowKey];
        const delta = rowData.currentValue - rowData.originalValue;

        // Set saving state
        const savingRows = {...this.state.savingRows};
        savingRows[rowKey] = true;
        this.setState({savingRows});

        try {
            // Always create/update logs to mark as confirmed, even if delta is 0
            const sessionId = this.getSessionId();
            const sessionReason = `Inventory Session ${sessionId}`;
            const { packageLogs, inventoryLogs } = this.state.collaborativeState;
            
            // Handle package logs for assemblies and subassemblies
            if (type === 'assembly' || type === 'subassembly') {
                const assemblyService = ServiceLocator.Instance.getAssemblyService();
                
                // Check if a log already exists for this item and session
                const existingLog = packageLogs.find(log => 
                    log.reason === sessionReason &&
                    ((type === 'assembly' && log.assemblyId === id) ||
                     (type === 'subassembly' && log.subAssemblyId === id))
                );
                
                if (existingLog) {
                    // Update existing log quantity (add delta if there is one, or keep at 0 if no change)
                    const updatePayload = {
                        ...existingLog,
                        quantity: existingLog.quantity + delta,
                        logDate: toUtcForStorage(new Date())
                    };
                    await assemblyService.updatePackageLog(updatePayload);
                } else {
                    // Create new log (quantity will be 0 if no change, or delta if there is a change)
                    const payload = {
                        id: uuid(),
                        assemblyId: type === 'assembly' ? id : null,
                        subAssemblyId: type === 'subassembly' ? id : null,
                        quantity: delta,
                        logDate: toUtcForStorage(new Date()),
                        checkedBom: false,
                        reason: sessionReason
                    };
                    await assemblyService.createPackageLog(payload);
                }
            } else if (type === 'part') {
                const partService = ServiceLocator.Instance.getPartService();
                
                // Check if a log already exists for this part and session
                const existingLog = inventoryLogs.find(log => 
                    log.reason === sessionReason && log.partId === id
                );
                
                if (existingLog) {
                    // Update existing log quantity (add delta if there is one, or keep at 0 if no change)
                    const updatePayload = {
                        ...existingLog,
                        quantity: existingLog.quantity + delta,
                        logDate: toUtcForStorage(new Date())
                    };
                    await partService.updateInventoryLog(updatePayload);
                } else {
                    // Create new log (quantity will be 0 if no change, or delta if there is a change)
                    const payload = {
                        id: uuid(),
                        quantity: delta,
                        reason: sessionReason,
                        partId: id,
                        logDate: toUtcForStorage(new Date()),
                        userId: AuthenticationService.getInstance().getUserId()
                    };
                    await partService.createInventoryLog(payload);
                }
            }
            
            // Refresh collaborative state to get updated logs
            await this.loadCollaborativeState();

            // Mark row as confirmed and update original value for future delta calculations
            const confirmedRows = {...this.state.confirmedRows};
            const modifiedRows = {...this.state.modifiedRows};
            const manuallyModifiedRows = {...this.state.manuallyModifiedRows};
            confirmedRows[rowKey] = true;
            // Clear manual modification flag since it's now confirmed
            manuallyModifiedRows[rowKey] = false;
            modifiedRows[rowKey].originalValue = modifiedRows[rowKey].currentValue;
            
            this.setState({confirmedRows, modifiedRows, manuallyModifiedRows}, () => {
                this.saveStateToStorage();
            });

            // Refresh all data after confirmation to get updated quantities
            await Promise.all([
                this.loadAssemblies(),
                this.loadSubAssemblies(),
                this.loadParts()
            ]);

            window.$alertSuccess(`${type} quantity confirmed`);
        } catch (error) {
            console.error('Error confirming row:', error);
            window.$alertError(`Error confirming ${type}`);
        } finally {
            // Clear saving state
            const savingRows = {...this.state.savingRows};
            savingRows[rowKey] = false;
            this.setState({savingRows});
        }
    }

    unconfirmRow = async (rowKey, type, id, refresh = true) => {
        const self = this;

        if (refresh) {
            const confirm = window.confirm("Are you sure you want to reset this item?");
            if (!confirm) {
                return;
            }
        }

        // Set saving state
        const savingRows = {...this.state.savingRows};
        savingRows[rowKey] = true;
        this.setState({savingRows});

        try {
            const sessionId = this.getSessionId();
            const sessionReason = `Inventory Session ${sessionId}`;
            const { packageLogs, inventoryLogs } = this.state.collaborativeState;
            
            // Handle package logs for assemblies and subassemblies
            if (type === 'assembly' || type === 'subassembly') {
                const assemblyService = ServiceLocator.Instance.getAssemblyService();
                
                // Find existing log for this item and session
                const existingLog = packageLogs.find(log => 
                    log.reason === sessionReason &&
                    ((type === 'assembly' && log.assemblyId === id) ||
                     (type === 'subassembly' && log.subAssemblyId === id))
                );
                
                if (existingLog) {
                    // Delete the log entirely to unconfirm the row
                    await assemblyService.deletePackageLog(existingLog);
                }
            } else if (type === 'part') {
                const partService = ServiceLocator.Instance.getPartService();
                
                // Find existing log for this part and session
                const existingLog = inventoryLogs.find(log => 
                    log.reason === sessionReason && log.partId === id
                );
                
                if (existingLog) {
                    // Delete the log entirely to unconfirm the row
                    await partService.deleteInventoryLog(existingLog.id);
                }
            }

        } catch (error) {
            console.error('Error resetting row:', error);
            window.$alertError('Error resetting item');
        } finally {
            // Clear saving state
            const savingRows = {...this.state.savingRows};
            savingRows[rowKey] = false;
            this.setState({savingRows});
            // Mark row as unconfirmed and reset to original state
            const confirmedRows = {...this.state.confirmedRows};
            const modifiedRows = {...this.state.modifiedRows};
            const manuallyModifiedRows = {...this.state.manuallyModifiedRows};
            confirmedRows[rowKey] = false;
            // Reset to original value and clear manual modification flag
            manuallyModifiedRows[rowKey] = false;
            
            this.setState({confirmedRows, modifiedRows, manuallyModifiedRows}, () => {
                this.saveStateToStorage();
            });

            if (refresh) {
                window.$alertSuccess('Item reset successfully. Quantity will update momentarily...');
                setTimeout(() => {
                    this.syncDataFromDatabase();
                }, 1000);
            }
        }
    }

    setAsNonInventoryItem = async (rowKey, type, id) => {
        if (!window.confirm(`Set this ${type} as an inventory item?`)) {
            return;
        }

        // Set saving state
        const savingRows = {...this.state.savingRows};
        savingRows[rowKey] = true;
        this.setState({savingRows});

        try {
            // Get the full item object from state
            const item = this.getItemByRowKey(rowKey);
            if (!item) {
                throw new Error(`Item not found: ${rowKey}`);
            }

            if (type === 'assembly') {
                const assemblyService = ServiceLocator.Instance.getAssemblyService();
                const updatedAssembly = {
                    ...item.assembly,
                    isInventoryItem: false
                };
                await assemblyService.updateAssembly(updatedAssembly);
            } else if (type === 'subassembly') {
                const assemblyService = ServiceLocator.Instance.getAssemblyService();
                const updatedSubAssembly = {
                    ...item.subAssembly,
                    isInventoryItem: false
                };
                await assemblyService.updateSubAssembly(updatedSubAssembly);
            } else if (type === 'part') {
                const partService = ServiceLocator.Instance.getPartService();
                const updatedPart = {
                    ...item.part,
                    isInventoryItem: false
                };
                await partService.updatePart(updatedPart);
            }

            // Refresh data to get updated isInventoryItem values
            this.loadData();
            window.$alertSuccess(`${type} set as inventory item`);
            savingRows[rowKey] = false;
            this.setState({savingRows});
        } catch (error) {
            console.error('Error setting as inventory item:', error);
            window.$alertError(`Error setting ${type} as inventory item`);

            // Set saving state
            const savingRows = {...this.state.savingRows};
            savingRows[rowKey] = false;
            this.setState({savingRows});
        }
    };

    getRowStyle = (rowKey) => {
        if (this.isRowSaving(rowKey)) {
            return { backgroundColor: '#e3f2fd' };
        } else if (this.isRowConfirmed(rowKey)) {
            return { backgroundColor: '#e8f5e8' };
        } else if (this.isRowModified(rowKey)) {
            return { backgroundColor: '#fff3e0' };
        }
        return {};
    }

    renderAssembliesList = (filteredAssemblies) => {
        return (
            <div>
                {filteredAssemblies.map((assembly) => {
                    const rowKey = `assembly|${assembly.assembly.id}`;
                    const rowData = this.state.modifiedRows[rowKey];
                    const cardClass = this.isRowSaving(rowKey) ? 'saving' : 
                                    this.isRowConfirmed(rowKey) ? 'confirmed' : 
                                    this.isRowModified(rowKey) ? 'modified' : '';

                    return (
                        <Card key={assembly.assembly.id} className={`${this.props.classes.itemCard} ${cardClass}`}>
                            <CardContent style={{padding: '4px'}}>
                                <div className={this.props.classes.itemHeader}>
                                    <div style={{display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
                                        <Checkbox
                                            checked={this.state.selectedRows[rowKey] || false}
                                            onChange={(e) => this.handleRowSelection(rowKey, e.target.checked)}
                                            size="small"
                                            style={{padding: '2px', marginTop: '-2px'}}
                                        />
                                        <div>
                                            <div className={this.props.classes.itemTitle}>
                                                {assembly.assembly.assemblyId} rev {assembly.assembly.revision}
                                            </div>
                                            <div className={this.props.classes.itemSubtitle}>
                                                {assembly.assembly.description}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className={this.props.classes.quantitySection}>
                                    <TextField
                                        type="number"
                                        value={rowData ? rowData.currentValue : 0}
                                        onChange={(e) => this.handleQuantityChange(rowKey, e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        style={{width: 100}}
                                        inputProps={{ min: 0 }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => this.openCalculator(rowKey, `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}`)}
                                        style={{padding: 4}}
                                    >
                                        <FontAwesomeIcon icon={faCalculator} size="sm" />
                                    </IconButton>
                                </div>
                                
                                <div className={this.props.classes.actionSection}>
                                    {this.isRowSaving(rowKey) ? 
                                        <CircularProgress size={20} />
                                        :
                                        <div style={{alignItems: 'center'}}>
                                            {this.isRowConfirmed(rowKey) ? (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() => this.unconfirmRow(rowKey, 'assembly', assembly.assembly.id)}
                                                        className="warning-button-sm"
                                                        size="small"
                                                        startIcon={<FontAwesomeIcon icon={faUndo} />}
                                                    >
                                                        Reset
                                                    </Button>
                                                    {this.isRowModified(rowKey) && (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => this.confirmRow(rowKey, 'assembly', assembly.assembly.id)}
                                                            className="success-button-sm"
                                                            size="small"
                                                            startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                            style={{marginLeft: '8px'}}
                                                        >
                                                            Update
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => this.confirmRow(rowKey, 'assembly', assembly.assembly.id)}
                                                    className="success-button-sm"
                                                    size="small"
                                                    startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                >
                                                    Confirm
                                                </Button>
                                            )}
                                            {!assembly.assembly.isInventoryItem && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.setAsNonInventoryItem(rowKey, 'assembly', assembly.assembly.id)}
                                                    style={{color: '#f44336', marginLeft: '8px'}}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </IconButton>
                                            )}
                                        </div>
                                    }
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        );
    }

    renderSubAssembliesList = (filteredSubAssemblies) => {
        return (
            <div>
                {filteredSubAssemblies.map((subAssembly) => {
                    const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                    const rowData = this.state.modifiedRows[rowKey];
                    const cardClass = this.isRowSaving(rowKey) ? 'saving' : 
                                    this.isRowConfirmed(rowKey) ? 'confirmed' : 
                                    this.isRowModified(rowKey) ? 'modified' : '';

                    return (
                        <Card key={subAssembly.subAssembly.id} className={`${this.props.classes.itemCard} ${cardClass}`}>
                            <CardContent style={{padding: '4px'}}>
                                <div className={this.props.classes.itemHeader}>
                                    <div style={{display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
                                        <Checkbox
                                            checked={this.state.selectedRows[rowKey] || false}
                                            onChange={(e) => this.handleRowSelection(rowKey, e.target.checked)}
                                            size="small"
                                            style={{padding: '2px', marginTop: '-2px'}}
                                        />
                                        <div>
                                            <div className={this.props.classes.itemTitle}>
                                                {subAssembly.subAssembly.subAssemblyId} rev {subAssembly.subAssembly.revision}
                                            </div>
                                            <div className={this.props.classes.itemSubtitle}>
                                                {subAssembly.subAssembly.description}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className={this.props.classes.quantitySection}>
                                    <TextField
                                        type="number"
                                        value={rowData ? rowData.currentValue : 0}
                                        onChange={(e) => this.handleQuantityChange(rowKey, e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        style={{width: 100}}
                                        inputProps={{ min: 0 }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => this.openCalculator(rowKey, `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}`)}
                                        style={{padding: 4}}
                                    >
                                        <FontAwesomeIcon icon={faCalculator} size="sm" />
                                    </IconButton>
                                </div>
                                
                                <div className={this.props.classes.actionSection}>
                                    {this.isRowSaving(rowKey) ? 
                                        <CircularProgress size={20} />
                                        :
                                        <div style={{alignItems: 'center'}}>
                                            {this.isRowConfirmed(rowKey) ? (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() => this.unconfirmRow(rowKey, 'subassembly', subAssembly.subAssembly.id)}
                                                        className="warning-button-sm"
                                                        size="small"
                                                        startIcon={<FontAwesomeIcon icon={faUndo} />}
                                                    >
                                                        Reset
                                                    </Button>
                                                    {this.isRowModified(rowKey) && (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => this.confirmRow(rowKey, 'subassembly', subAssembly.subAssembly.id)}
                                                            className="success-button-sm"
                                                            size="small"
                                                            startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                            style={{marginLeft: '8px'}}
                                                        >
                                                            Update
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => this.confirmRow(rowKey, 'subassembly', subAssembly.subAssembly.id)}
                                                    className="success-button-sm"
                                                    size="small"
                                                    startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                >
                                                    Confirm
                                                </Button>
                                            )}
                                            {!subAssembly.subAssembly.isInventoryItem && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.setAsNonInventoryItem(rowKey, 'subassembly', subAssembly.subAssembly.id)}
                                                    style={{color: '#f44336', marginLeft: '8px'}}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </IconButton>
                                            )}
                                        </div>
                                    }
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        );
    }

    renderPartsList = (filteredParts) => {
        return (
            <div>
                {filteredParts.map((part) => {
                    const rowKey = `part|${part.part.id}`;
                    const rowData = this.state.modifiedRows[rowKey];
                    const cardClass = this.isRowSaving(rowKey) ? 'saving' : 
                                    this.isRowConfirmed(rowKey) ? 'confirmed' : 
                                    this.isRowModified(rowKey) ? 'modified' : '';

                    return (
                        <Card key={part.part.id} className={`${this.props.classes.itemCard} ${cardClass}`}>
                            <CardContent style={{padding: '4px'}}>
                                <div className={this.props.classes.itemHeader}>
                                    <div style={{display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
                                        <Checkbox
                                            checked={this.state.selectedRows[rowKey] || false}
                                            onChange={(e) => this.handleRowSelection(rowKey, e.target.checked)}
                                            size="small"
                                            style={{padding: '2px', marginTop: '-2px'}}
                                        />
                                        <div>
                                            <div className={this.props.classes.itemTitle}>
                                                {part.part.partId} rev {part.part.revision}
                                            </div>
                                            <div className={this.props.classes.itemSubtitle}>
                                                {part.part.description}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className={this.props.classes.quantitySection}>
                                    <TextField
                                        type="number"
                                        value={rowData ? rowData.currentValue : 0}
                                        onChange={(e) => this.handleQuantityChange(rowKey, e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        style={{width: 100}}
                                        inputProps={{ min: 0 }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => this.openCalculator(rowKey, `${part.part.partId} rev ${part.part.revision}`)}
                                        style={{padding: 4}}
                                    >
                                        <FontAwesomeIcon icon={faCalculator} size="sm" />
                                    </IconButton>
                                </div>
                                
                                <div className={this.props.classes.actionSection}>
                                    {this.isRowSaving(rowKey) ? 
                                        <CircularProgress size={20} />
                                        :
                                        <div style={{alignItems: 'center'}}>
                                            {this.isRowConfirmed(rowKey) ? (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() => this.unconfirmRow(rowKey, 'part', part.part.id)}
                                                        className="warning-button-sm"
                                                        size="small"
                                                        startIcon={<FontAwesomeIcon icon={faUndo} />}
                                                    >
                                                        Reset
                                                    </Button>
                                                    {this.isRowModified(rowKey) && (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => this.confirmRow(rowKey, 'part', part.part.id)}
                                                            className="success-button-sm"
                                                            size="small"
                                                            startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                            style={{marginLeft: '8px'}}
                                                        >
                                                            Confirm
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => this.confirmRow(rowKey, 'part', part.part.id)}
                                                    className="success-button-sm"
                                                    size="small"
                                                    startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                >
                                                    Confirm
                                                </Button>
                                            )}
                                            {!part.part.isInventoryItem && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.setAsNonInventoryItem(rowKey, 'part', part.part.id)}
                                                    style={{color: '#f44336', marginLeft: '8px'}}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </IconButton>
                                            )}
                                        </div>
                                    }
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        );
    }

    getSessionId = () => {
        return this.props.match.params.sessionId || 'default';
    }

    copyUrl = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url).then(() => {
            window.$alertSuccess("URL copied to clipboard");
        }).catch(() => {
            window.$alertError("Failed to copy URL");
        });
    }

    areAllItemsConfirmed = () => {
        const allItems = [
            ...this.state.assemblies.map(a => `assembly|${a.assembly.id}`),
            ...this.state.subAssemblies.map(sa => `subassembly|${sa.subAssembly.id}`),
            ...this.state.parts.map(p => `part|${p.part.id}`)
        ];

        return allItems.every(rowKey => this.isRowConfirmed(rowKey));
    }

    confirmAndEndSession = () => {
        if (window.confirm("Are you sure you want to end this inventory session?")) {
            window.$alertSuccess("Inventory session completed successfully");
            globalProps.history.push('/admin/inventory-setup');
        }
    }

    render() {
        const { classes } = this.props;
        const { loading, activeTab, assemblies, subAssemblies, parts, hideConfirmedRows } = this.state;
        const sessionId = this.getSessionId();

        if (loading) {
            return (
                <WorkshopTemplate>
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px'}}>
                        <CircularProgress/>
                    </div>
                </WorkshopTemplate>
            );
        }

        // Filter and sort assemblies
        let filteredAssemblies = [...assemblies].sort((a, b) => {
            const aId = a.assembly.assemblyId || '';
            const bId = b.assembly.assemblyId || '';
            if (aId !== bId) {
                return aId.localeCompare(bId);
            }
            const aRev = a.assembly.revision || '';
            const bRev = b.assembly.revision || '';
            return aRev.localeCompare(bRev);
        }).filter(assembly => {
            const rowKey = `assembly|${assembly.assembly.id}`;
            // Apply hide confirmed rows filter
            if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                return false;
            }
            
            // Apply inventory item filter
            if (!this.state.showNonInventoryItems && !assembly.assembly.isInventoryItem) {
                return false;
            }
            
            // Apply search filter
            if (this.state.searchTerm) {
                const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                if (searchTerms.length > 0) {
                    const searchFilter = searchTerms.every(term => {
                        const searchTerm = term.toLowerCase();
                        const idMatch = assembly.assembly.assemblyId && assembly.assembly.assemblyId.toLowerCase().includes(searchTerm);
                        const descriptionMatch = assembly.assembly.description && assembly.assembly.description.toLowerCase().includes(searchTerm);
                        return idMatch || descriptionMatch;
                    });
                    if (!searchFilter) {
                        return false;
                    }
                }
            }
            
            // Assemblies are always top-level, so always show them (no parent check needed)
            return true;
        });

        // Filter and sort subassemblies
        let filteredSubAssemblies = [...subAssemblies].sort((a, b) => {
            const aId = a.subAssembly.subAssemblyId || '';
            const bId = b.subAssembly.subAssemblyId || '';
            if (aId !== bId) {
                return aId.localeCompare(bId);
            }
            const aRev = a.subAssembly.revision || '';
            const bRev = b.subAssembly.revision || '';
            return aRev.localeCompare(bRev);
        }).filter(subAssembly => {
            const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
            // Apply hide confirmed rows filter
            if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                return false;
            }
            
            // Apply inventory item filter
            if (!this.state.showNonInventoryItems && !subAssembly.subAssembly.isInventoryItem) {
                return false;
            }
            
            // Apply search filter
            if (this.state.searchTerm) {
                const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                if (searchTerms.length > 0) {
                    const searchFilter = searchTerms.every(term => {
                        const searchTerm = term.toLowerCase();
                        const idMatch = subAssembly.subAssembly.subAssemblyId && subAssembly.subAssembly.subAssemblyId.toLowerCase().includes(searchTerm);
                        const descriptionMatch = subAssembly.subAssembly.description && subAssembly.subAssembly.description.toLowerCase().includes(searchTerm);
                        return idMatch || descriptionMatch;
                    });
                    if (!searchFilter) {
                        return false;
                    }
                }
            }
            
            // Check if parents are confirmed (only show if parents are confirmed or if it's top-level)
            const parentsConfirmed = this.areParentsConfirmed('subassembly', subAssembly.subAssembly.id);
            return parentsConfirmed;
        });

        // Filter and sort parts
        let filteredParts = [...parts].sort((a, b) => {
            const aId = a.part.partId || '';
            const bId = b.part.partId || '';
            if (aId !== bId) {
                return aId.localeCompare(bId);
            }
            const aRev = a.part.revision || '';
            const bRev = b.part.revision || '';
            return aRev.localeCompare(bRev);
        }).filter(part => {
            const rowKey = `part|${part.part.id}`;
            // Apply hide confirmed rows filter
            if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                return false;
            }
            
            // Apply inventory item filter
            if (!this.state.showNonInventoryItems && !part.part.isInventoryItem) {
                return false;
            }
            
            // Apply search filter
            if (this.state.searchTerm) {
                const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                if (searchTerms.length > 0) {
                    const searchFilter = searchTerms.every(term => {
                        const searchTerm = term.toLowerCase();
                        const idMatch = part.part.partId && part.part.partId.toLowerCase().includes(searchTerm);
                        const descriptionMatch = part.part.description && part.part.description.toLowerCase().includes(searchTerm);
                        return idMatch || descriptionMatch;
                    });
                    if (!searchFilter) {
                        return false;
                    }
                }
            }
            
            // Check if parents are confirmed (only show if parents are confirmed or if it's top-level)
            if (!this.areParentsConfirmed('part', part.part.id)) {
                return false;
            }
            return true;
        });

        // Calculate counts from filtered data
        const assemblyCount = filteredAssemblies.length;
        const subAssemblyCount = filteredSubAssemblies.length;
        const partCount = filteredParts.length;

        return (
            <WorkshopTemplate>
                <div style={{padding: '0 8px'}}>
                    {/* Header */}
                    <div style={{textAlign: 'center', marginBottom: '0'}}>
                        <Typography variant="h6" style={{marginBottom: '0'}}>
                            Inventory Session {sessionId}
                        </Typography>
                        <div style={{marginTop: '8px'}}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={this.syncDataFromDatabase}
                                disabled={this.state.syncing}
                                size="small"
                                style={{minWidth: 120, backgroundColor: '#FFF'}}
                                startIcon={this.state.syncing ? <CircularProgress size={16} /> : <FontAwesomeIcon icon={faSync} />}
                            >
                                {this.state.syncing ? 'Syncing...' : 'Sync Database'}
                            </Button>
                            <div style={{marginTop: '4px', fontSize: '11px', color: '#666'}}>
                                Updates quantities from packaging, inventory, and sales logs
                            </div>
                        </div>
                    </div>
                    
                    {/* Search Bar */}
                    <div style={{marginTop: '8px'}}>
                        <TextField
                            value={this.state.searchTerm || ''}
                            onChange={this.searchInventory}
                            label="Search PN or Description..."
                            variant="outlined"
                            size="small"
                            fullWidth
                            style={{backgroundColor: 'white'}}
                        />
                    </div>
                    
                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 1 }}>
                        <Tabs value={activeTab} onChange={this.handleTabChange} aria-label="inventory session tabs" variant="fullWidth">
                            <Tab 
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span style={{fontSize: '12px'}}>Assemblies</span>
                                        <Chip size="small" label={assemblyCount} style={{height: '20px', fontSize: '10px'}} />
                                    </Box>
                                } 
                            />
                            <Tab 
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span style={{fontSize: '12px'}}>Subassemblies</span>
                                        <Chip size="small" label={subAssemblyCount} style={{height: '20px', fontSize: '10px'}} />
                                    </Box>
                                } 
                            />
                            <Tab 
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span style={{fontSize: '12px'}}>Parts</span>
                                        <Chip size="small" label={partCount} style={{height: '20px', fontSize: '10px'}} />
                                    </Box>
                                } 
                            />
                        </Tabs>
                    </Box>

                    {/* Filter and Selection Controls */}
                    <div style={{marginBottom: '8px', marginTop: '8px', justifyContent: 'center', gap: '8px', flexWrap: 'wrap'}}>
                        <div className={classes.filterToggle} style={{marginBottom: 5}}>
                            <Checkbox
                                checked={this.state.hideConfirmedRows}
                                onChange={this.toggleHideConfirmedRows}
                                style={{color: '#35d2d9'}}
                            />
                            <span className={classes.filterLabel}>Hide Confirmed</span>
                        </div>
                        <div className={classes.filterToggle} style={{marginBottom: 5}}>
                            <Checkbox
                                checked={this.state.showNonInventoryItems}
                                onChange={this.toggleShowNonInventoryItems}
                                style={{color: '#35d2d9'}}
                            />
                            <span className={classes.filterLabel}>Show Non-Inventory Items</span>
                        </div>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={this.handleSelectAll}
                            style={{minWidth: 80, fontSize: '12px', backgroundColor: 'white', marginRight: 5}}
                        >
                            Select All
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={this.handleSelectNone}
                            style={{minWidth: 80, fontSize: '12px', backgroundColor: 'white'}}
                        >
                            Select None
                        </Button>
                    </div>

                    {/* Sync Status */}
                    {this.state.collaborativeState.lastRefresh && (
                        <div style={{textAlign: 'center', marginBottom: '8px', fontSize: '11px', color: '#666'}}>
                            Last sync: {this.state.collaborativeState.lastRefresh.toLocaleTimeString()}
                        </div>
                    )}

                    {this.state.syncing && <div><CircularProgress size={16} /> Syncing...</div>}

                                            <TabPanel value={activeTab} index={0}>
                            {this.renderAssembliesList(filteredAssemblies)}
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            {this.renderSubAssembliesList(filteredSubAssemblies)}
                        </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            {this.getHiddenPartsMatchingSearch().length > 0 && (
                                <div style={{
                                    marginBottom: 12,
                                    padding: 12,
                                    backgroundColor: '#fff8e1',
                                    border: '1px solid #ffe082',
                                    borderRadius: 4,
                                    fontSize: 13,
                                }}>
                                    <strong>Parts hidden by session rules:</strong>
                                    <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                                        {this.getHiddenPartsMatchingSearch().map((partRow) => (
                                            <li key={partRow.part.id} style={{ marginBottom: 6 }}>
                                                <strong>{partRow.part.partId}</strong>
                                                {this.getPartSessionBlockers(partRow).map((reason) => (
                                                    <div key={reason} style={{ color: '#666' }}>{reason}</div>
                                                ))}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {this.renderPartsList(filteredParts)}
                        </TabPanel>

                        {/* Confirm & End Session Button */}
                        {this.areAllItemsConfirmed() && (
                            <div style={{margin: '12px 0', textAlign: 'center'}}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.confirmAndEndSession}
                                    className="success-button-lg"
                                    style={{minWidth: 200, fontSize: '16px'}}
                                >
                                    Confirm & End Session
                                </Button>
                            </div>
                        )}
                </div>

                {/* Floating Action Button for Selected Rows */}
                {Object.keys(this.state.selectedRows).some(key => this.state.selectedRows[key]) && (() => {
                    const selectedRowKeys = Object.keys(this.state.selectedRows).filter(key => this.state.selectedRows[key]);
                    const allConfirmed = selectedRowKeys.length > 0 && selectedRowKeys.every(key => this.isRowConfirmed(key));
                    
                    return (
                        <div style={{
                            position: 'fixed',
                            bottom: '20px',
                            right: '20px',
                            zIndex: 1000
                        }}>
                            <Button
                                variant="contained"
                                color={allConfirmed ? "secondary" : "primary"}
                                onClick={allConfirmed ? this.handleResetSelected : this.handleConfirmSelected}
                                style={{
                                    minWidth: 200,
                                    fontSize: '14px',
                                    padding: '12px 16px',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                }}
                                startIcon={<FontAwesomeIcon icon={faCheck} />}
                            >
                                {allConfirmed ? 'Reset Selected' : 'Confirm Selected'} ({selectedRowKeys.length})
                            </Button>
                        </div>
                    );
                })()}

                {/* Calculator Modal */}
                <Dialog 
                    open={this.state.calculatorOpen} 
                    onClose={this.closeCalculator}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>
                        Calculator
                        <Typography variant="subtitle2" style={{color: '#666', marginTop: '4px'}}>
                            {this.state.calculatorTitle}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <div style={{marginBottom: '16px'}}>
                            {this.state.calculatorLines.map((line, index) => (
                                <TextField
                                    key={index}
                                    type="number"
                                    value={line === 0 ? '' : line}
                                    onChange={(e) => this.updateCalculatorLine(index, e.target.value)}
                                    onKeyDown={(e) => this.handleCalculatorKeyPress(e, index)}
                                    onFocus={(e) => e.target.select()}
                                    inputProps={{
                                        'data-calculator-index': index,
                                        min: 0,
                                        step: 'any'
                                    }}
                                    variant="outlined"
                                    size="small"
                                    style={{width: '100%', marginBottom: '8px'}}
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>
                        <Button
                            variant="outlined"
                            onClick={this.addCalculatorLine}
                            style={{width: '100%', marginBottom: '16px'}}
                        >
                            Add Line
                        </Button>
                        <div style={{
                            backgroundColor: '#f5f5f5',
                            padding: '12px',
                            borderRadius: '4px',
                            textAlign: 'center',
                            fontSize: '18px',
                            fontWeight: 'bold'
                        }}>
                            Total: {this.state.calculatorTotal}
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeCalculator}>
                            Cancel
                        </Button>
                        <Button onClick={this.confirmCalculator} color="primary" variant="contained">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </WorkshopTemplate>
        );
    }
}

InventorySession.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(InventorySession); 